import React, { useMemo, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiFetch, apiPost, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdFeldReihe, LdEingabe, LdTextfeld, LdAuswahl, LdDatum, LdChips, LdSchalterZeile, LdFensterFuss } from '../../components/ld/LdFenster';
import { useSchueler } from '../../contexts/SchuelerContext';
import {
  Notiz, WAEHLBARE_KATEGORIEN, katInfo, valenzAus, quelleInfo, ttmmUtc, notizText,
  setzeArchiv, kopiereNotizen, loescheMitRueckgaengig, signalNotizenGeaendert,
} from './notizen-lib';
import './Notizen.css';

// Welle U4b: Bearbeiten-Sheet (auch für neue Notizen). notiz = null → neue Notiz.
export function NotizSheet({ notiz, vorbelegtSchuelerId, onClose }: {
  notiz: Notiz | null;
  vorbelegtSchuelerId?: number | null;
  onClose: () => void;
}) {
  const { schuelerListe } = useSchueler();
  const neu = notiz === null;
  const heute = new Date().toLocaleDateString('sv-SE');
  const [schuelerId, setSchuelerId] = useState<number | ''>(notiz?.schueler_id ?? vorbelegtSchuelerId ?? '');
  const [datum, setDatum] = useState(notiz?.datum?.slice(0, 10) ?? heute);
  const [fach, setFach] = useState(notiz?.fach ?? '');
  const [kategorie, setKategorie] = useState(notiz?.kategorie ?? 'Sonstiges');
  const [text, setText] = useState(notiz ? notizText(notiz) : '');
  const [angepinnt, setAngepinnt] = useState(!!notiz?.angepinnt);
  const [laeuft, setLaeuft] = useState(false);

  const kategorien = useMemo(() => (WAEHLBARE_KATEGORIEN.includes(kategorie) ? WAEHLBARE_KATEGORIEN : [...WAEHLBARE_KATEGORIEN, kategorie]), [kategorie]);
  const schuelerOptionen = useMemo(() => [...schuelerListe].sort((a, b) => (a.klasse || '').localeCompare(b.klasse || '') || a.name.localeCompare(b.name, 'de')), [schuelerListe]);
  const q = notiz ? quelleInfo(notiz.quelle) : null;

  async function speichern() {
    if (!schuelerId) { toast('Bitte eine Schülerin oder einen Schüler wählen.', 'error'); return; }
    if (!text.trim()) { toast('Der Text der Notiz darf nicht leer sein.', 'error'); return; }
    setLaeuft(true);
    try {
      if (neu) {
        await apiPost('/api/notizen', {
          schueler_id: schuelerId, datum, fach: fach.trim() || null, kategorie,
          zusammenfassung: text.trim(), valenz: valenzAus(kategorie), angepinnt, quelle: 'manuell',
        });
        toast('Notiz gespeichert', 'success');
      } else {
        await apiFetch(`/api/notizen/${notiz!.id}`, { method: 'PUT', body: JSON.stringify({
          datum, fach: fach.trim() || null, kategorie, zusammenfassung: text.trim(), valenz: valenzAus(kategorie),
        }) });
        if (angepinnt !== !!notiz!.angepinnt)
          await apiFetch(`/api/notizen/${notiz!.id}`, { method: 'PATCH', body: JSON.stringify({ angepinnt }) });
        toast('Notiz gespeichert', 'success');
      }
      signalNotizenGeaendert();
      onClose();
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
    finally { setLaeuft(false); }
  }

  async function archivUmschalten() {
    if (!notiz) return;
    try {
      await setzeArchiv(notiz, !notiz.archiviert);
      toast(notiz.archiviert ? 'Notiz zurückgeholt' : 'Notiz archiviert', 'success');
      signalNotizenGeaendert();
      onClose();
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
  }

  const kontext = neu ? null
    : `${notiz!.schueler_name}${notiz!.klasse_name ? `, ${notiz!.klasse_name}` : ''}${q ? `, ${q.text}` : ''}${notiz!.created_at ? `, erfasst am ${ttmmUtc(notiz!.created_at)}` : ''}`;

  return (
    <LdSheet open onClose={onClose} maxWidth={560}
      kopf={{ titel: neu ? 'Neue Notiz' : 'Notiz bearbeiten', kontext }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt="Speichern" onHaupt={speichern} laden={laeuft} />}>
      {neu && (
        <LdFeld label="Schüler/in">
          <LdAuswahl aria-label="Schüler/in" value={schuelerId} placeholder="Schüler/in wählen" onChange={e => setSchuelerId(e.target.value ? Number(e.target.value) : '')}>
            {schuelerOptionen.map(s => <option key={s.id} value={s.id}>{s.name}{s.klasse ? `, ${s.klasse}` : ''}</option>)}
          </LdAuswahl>
        </LdFeld>
      )}

      <LdFeldReihe>
        <LdFeld label="Datum">
          <LdDatum aria-label="Datum" required value={datum} onChange={e => setDatum(e.target.value)} />
        </LdFeld>
        <LdFeld label="Fach" optional htmlFor="ld-nz-fach">
          <LdEingabe id="ld-nz-fach" value={fach} onChange={e => setFach(e.target.value)} placeholder="z. B. Mathe" />
        </LdFeld>
      </LdFeldReihe>

      <LdFeld label="Kategorie">
        <LdChips aria-label="Kategorie" wert={kategorie} onChange={setKategorie}
          optionen={kategorien.map(k => { const i = katInfo(k); return { wert: k, label: i.label, icon: i.icon }; })} />
      </LdFeld>

      <LdFeld label="Notiz" htmlFor="ld-nz-text">
        <LdTextfeld id="ld-nz-text" rows={4} value={text} onChange={e => setText(e.target.value)} placeholder="Was ist dir aufgefallen?" />
      </LdFeld>

      <LdSchalterZeile icon="push_pin" titel="Anpinnen" text="Bleibt oben, auch im Profil" an={angepinnt} onChange={setAngepinnt} />

      {!neu && (
        <div className="ld-nz-weitere">
          <button type="button" className="ld-kl-link" onClick={() => kopiereNotizen([notiz!])}><MIcon name="content_copy" size={17} />Kopieren</button>
          <button type="button" className="ld-kl-link" onClick={archivUmschalten}><MIcon name={notiz!.archiviert ? 'unarchive' : 'archive'} size={17} />{notiz!.archiviert ? 'Zurückholen' : 'Archivieren'}</button>
          <button type="button" className="ld-kl-link" style={{ color: 'var(--ld-danger-text)' }} onClick={() => { onClose(); loescheMitRueckgaengig(notiz!); }}><MIcon name="delete" size={17} />Löschen</button>
        </div>
      )}
    </LdSheet>
  );
}
